import React, { type ForwardedRef, useEffect, useImperativeHandle, useRef, useState } from 'react';

import { useAppSelector, useLiquidId } from '@pkg/hooks';

import LiquidIDItemDetails from '../LiquidIDItemDetails';
import TriggerButton from '../TriggerButton';

import { LiquidIDCanvasWrapper, StyledWebGL } from './styles';

interface Props {
    className?: string;
    editMode?: boolean;
    standalone?: boolean;
    isYativerse?: boolean;
    forwardedRef?: ForwardedRef<HTMLDivElement>;
    showButton?: boolean;
    isScrollingElement?: boolean;
    backgroundVisibility?: boolean;
}

const LiquidIDCube = ({
    className,
    editMode,
    standalone,
    forwardedRef,
    isScrollingElement,
    showButton = true,
    backgroundVisibility = true,
}: Props) => {
    const [isAnimating, setIsAnimating] = useState(false);
    const [isHoveringCube, setIsHoveringCube] = useState(false);
    const liquidIDExpandedPreview = useAppSelector((state) => state.yatPage.liquidIDExpandedPreview);
    const editingLiquidID = useAppSelector((state) => state.yatPage.editStates.editingLiquidID);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const { handleExpandLiquidID } = useLiquidId({
        wrapperRef,
        editMode,
        standalone,
        isScrollingElement,
        backgroundVisibility,
    });

    useImperativeHandle(forwardedRef, () => wrapperRef.current as HTMLDivElement);

    useEffect(() => {
        if (!liquidIDExpandedPreview && !editingLiquidID) {
            const timeout = setTimeout(() => {
                setIsAnimating(false);
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [liquidIDExpandedPreview, editMode, editingLiquidID]);

    return (
        <LiquidIDCanvasWrapper
            className={className}
            $standalone={standalone}
            onMouseEnter={() => {
                if (!isAnimating && !liquidIDExpandedPreview) setIsHoveringCube(true);
            }}
            onMouseLeave={() => {
                if (!isAnimating && !liquidIDExpandedPreview) setIsHoveringCube(false);
            }}
            $mobilePointersAllowed={liquidIDExpandedPreview || standalone}
        >
            {!isAnimating && showButton && !standalone && (
                <TriggerButton isHovering={isHoveringCube} editMode={editMode} />
            )}
            {!editMode && !liquidIDExpandedPreview && <LiquidIDItemDetails expanded={false} />}
            <StyledWebGL ref={wrapperRef} onClick={handleExpandLiquidID} $noPointer={editingLiquidID} />
        </LiquidIDCanvasWrapper>
    );
};

export default LiquidIDCube;
